import React, { createContext, useContext, useEffect, useState } from 'react'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'regis-form-ui-theme',
  ...props
}: ThemeProviderProps) {
  // Force light mode only - ignore localStorage and system preferences
  const [theme] = useState<Theme>('light')

  useEffect(() => {
    const root = window.document.documentElement

    // Always remove dark mode class and ensure light mode
    root.classList.remove('dark')
    root.classList.add('light')
  }, [])

  const value = {
    theme,
    // setTheme is a no-op since we're forcing light mode
    setTheme: () => {
      // Do nothing - theme is locked to light mode
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider')

  return context
}
